var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router";
import useVaultAccounts from "shared/lib/hooks/useVaultAccounts";
import { getAssetDisplay } from "shared/lib/utils/asset";
import { formatBigNumber, isPracticallyZero } from "shared/lib/utils/math";
import VaultV2MigrationForm from "./v2/VaultV2MigrationForm";
import theme from "shared/lib/designSystem/theme";
import colors from "shared/lib/designSystem/colors";
import { Title, PrimaryText, SecondaryText } from "shared/lib/designSystem";
import { getVaultColor } from "shared/lib/utils/vault";
import VaultV2DepositWithdrawForm from "./v2/VaultV2DepositWithdrawForm";
import useVaultActionForm from "../../../hooks/useVaultActionForm";
import { ACTIONS } from "./Modal/types";
import { useV2VaultData } from "shared/lib/hooks/web3DataContext";
import { ActionButton } from "shared/lib/components/Common/buttons";
import { getVaultURI } from "../../../constants/constants";
import { WithdrawIcon } from "shared/lib/assets/icons/icons";
import { RibbonVaultMigrationMap, isDisabledVault, } from "shared/lib/constants/constants";
import useVaultPriceHistory from "shared/lib/hooks/useVaultPerformanceUpdate";
import { BigNumber } from "ethers";
import { parseUnits } from "ethers/lib/utils";
var FormContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  flex-direction: column;\n  border-radius: ", ";\n  background: ", ";\n  z-index: 1;\n"], ["\n  display: flex;\n  align-items: center;\n  flex-direction: column;\n  border-radius: ", ";\n  background: ", ";\n  z-index: 1;\n"])), theme.border.radius, colors.background.two);
var FormExtraContainer = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  background: ", ";\n  padding: 32px 24px 16px 24px;\n  margin-top: -20px;\n  border-radius: ", ";\n  text-align: center;\n  z-index: 0;\n"], ["\n  background: ", ";\n  padding: 32px 24px 16px 24px;\n  margin-top: -20px;\n  border-radius: ", ";\n  text-align: center;\n  z-index: 0;\n"])), colors.background.three, theme.border.radius);
var CompleteWithdrawLogo = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 64px;\n  height: 64px;\n  border-radius: 100px;\n  background: ", "14;\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 64px;\n  height: 64px;\n  border-radius: 100px;\n  background: ", "14;\n"])), function (props) { return props.color; });
var CompleteWithdrawTitle = styled(Title)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  letter-spacing: 1px;\n"], ["\n  letter-spacing: 1px;\n"])));
var VaultV2ActionsForm = function (_a) {
    var vaultOption = _a.vaultOption, onFormSubmit = _a.onFormSubmit;
    var history = useHistory();
    var v1VaultAccounts = useVaultAccounts("v1").vaultAccounts;
    var _b = useV2VaultData(vaultOption).data, asset = _b.asset, decimals = _b.decimals, depositBalanceInAsset = _b.depositBalanceInAsset, lockedBalanceInAsset = _b.lockedBalanceInAsset, round = _b.round, withdrawals = _b.withdrawals;
    var priceHistory = useVaultPriceHistory(vaultOption, "v2").priceHistory;
    var _c = useState(false), hideMigrationForm = _c[0], setHideMigrationForm = _c[1];
    var _d = useState(false), hideCompleteWithdrawReminder = _d[0], setHideCompleteWithdrawReminder = _d[1];
    var color = getVaultColor(vaultOption);
    var _e = useVaultActionForm(vaultOption), vaultActionForm = _e.vaultActionForm, handleActionTypeChange = _e.handleActionTypeChange;
    var withdrawalAmount = useMemo(function () {
        var _a;
        return withdrawals.shares
            .mul(((_a = priceHistory.find(function (history) { return history.round === withdrawals.round; })) === null || _a === void 0 ? void 0 : _a.pricePerShare) || BigNumber.from(0))
            .div(parseUnits("1", decimals));
    }, [decimals, priceHistory, withdrawals.round, withdrawals.shares]);
    var migrateSourceVault = useMemo(function () {
        var _a, _b;
        return (_b = (_a = RibbonVaultMigrationMap[vaultOption]) === null || _a === void 0 ? void 0 : _a.v2) === null || _b === void 0 ? void 0 : _b.find(function (_vaultOption) {
            return Boolean(v1VaultAccounts[_vaultOption] &&
                !isPracticallyZero(v1VaultAccounts[_vaultOption].totalBalance, decimals));
        });
    }, [decimals, v1VaultAccounts, vaultOption]);
    var canCompleteWithdraw = useMemo(function () {
        return (vaultActionForm.withdrawOption !== "instant" &&
            !withdrawals.shares.isZero() &&
            withdrawals.round !== round);
    }, [round, vaultActionForm.withdrawOption, withdrawals]);
    /**
     * Make sure action type cannot be migrate when user cannot migrate
     */
    useEffect(function () {
        if (!migrateSourceVault && vaultActionForm.actionType === "migrate") {
            handleActionTypeChange(ACTIONS.deposit, "v2");
        }
    }, [migrateSourceVault, vaultActionForm.actionType, handleActionTypeChange]);
    var content = useMemo(function () {
        /**
         * Show migration form if user has balance in v1
         */
        if (migrateSourceVault && !hideMigrationForm) {
            return (_jsx(VaultV2MigrationForm, { vaultOption: vaultOption, migrateSourceVault: migrateSourceVault, onFormSubmit: onFormSubmit, onHideForm: function () { return setHideMigrationForm(true); } }, void 0));
        }
        /**
         * Show withdrawal reminder
         */
        if (canCompleteWithdraw && !hideCompleteWithdrawReminder) {
            return (_jsxs("div", __assign({ className: "d-flex flex-column align-items-center p-4" }, { children: [_jsx(CompleteWithdrawLogo, __assign({ color: color, className: "mt-3" }, { children: _jsx(WithdrawIcon, { color: color, height: 32, width: 32 }, void 0) }), void 0), _jsx(CompleteWithdrawTitle, __assign({ fontSize: 22, lineHeight: 28, className: "mt-3 text-center" }, { children: "Complete your initiated withdrawal" }), void 0), _jsxs(PrimaryText, __assign({ fontSize: 14, lineHeight: 20, className: "mt-3 text-center", color: colors.text }, { children: ["You can now complete your withdrawal of", " ", formatBigNumber(withdrawalAmount, decimals), " ", getAssetDisplay(asset), " from the vault."] }), void 0), _jsx(ActionButton, __assign({ color: "red", className: "py-3 mt-4", onClick: function () {
                            setHideCompleteWithdrawReminder(true);
                            /**
                             * Push complete withdraw history
                             */
                            history.push(getVaultURI(vaultOption, "v2") +
                                "?initialAction=completeWithdraw");
                        } }, { children: "Complete Withdrawal" }), void 0), _jsx(SecondaryText, __assign({ className: "mt-4 ", color: colors.primaryText, role: "button", onClick: function () { return setHideCompleteWithdrawReminder(true); } }, { children: _jsx("u", { children: "Skip" }, void 0) }), void 0)] }), void 0));
        }
        return (_jsx(VaultV2DepositWithdrawForm, { vaultOption: vaultOption, onFormSubmit: onFormSubmit }, void 0));
    }, [
        asset,
        canCompleteWithdraw,
        migrateSourceVault,
        color,
        decimals,
        hideCompleteWithdrawReminder,
        hideMigrationForm,
        history,
        onFormSubmit,
        vaultOption,
        withdrawalAmount,
    ]);
    var formExtra = useMemo(function () {
        var formExtraText = undefined;
        if (migrateSourceVault && !hideMigrationForm) {
            formExtraText = (_jsx(_Fragment, { children: "IMPORTANT: Withdrawal fees do not apply for migrations from V1 to V2" }, void 0));
        }
        else if (!canCompleteWithdraw || hideCompleteWithdrawReminder) {
            switch (vaultActionForm.actionType) {
                case ACTIONS.deposit:
                    if (isDisabledVault(vaultOption)) {
                        formExtraText = undefined;
                    }
                    else {
                        formExtraText = (_jsx(_Fragment, { children: "Your deposit will be deployed in the vault\u2019s weekly strategy on Friday at 11am UTC" }, void 0));
                    }
                    break;
                case ACTIONS.withdraw:
                    if (!isPracticallyZero(depositBalanceInAsset, decimals) &&
                        vaultActionForm.vaultOption !== "rSOL-THETA") {
                        formExtraText =
                            vaultActionForm.withdrawOption === "instant" ? (_jsx(_Fragment, { children: "IMPORTANT: instant withdrawals are only available before 11am UTC on Friday for funds that have not been deployed in the vault's weekly strategy" }, void 0)) : (_jsxs(_Fragment, { children: ["You can withdraw", " ", formatBigNumber(depositBalanceInAsset, decimals), " ", getAssetDisplay(asset), " immediately via instant withdrawals because these funds have not yet been deployed in the vault\u2019s strategy"] }, void 0));
                        break;
                    }
                    if (!isPracticallyZero(lockedBalanceInAsset, decimals)) {
                        if (vaultActionForm.withdrawOption === "instant") {
                            formExtraText = (_jsx(_Fragment, { children: "Instant withdrawals are unavailable because your funds have been deployed in this week\u2019s vault strategy. To withdraw your funds you need to initiate a withdrawal using standard withdrawals." }, void 0));
                        }
                        break;
                    }
                    break;
                case ACTIONS.transfer:
                    formExtraText = (_jsxs(_Fragment, { children: ["Vault transfers are ", _jsx("strong", { children: "FREE" }, void 0), ": withdrawal fees are waived when you transfer funds between", " ", vaultActionForm.vaultOption, " and", vaultActionForm.receiveVault] }, void 0));
                    break;
            }
        }
        if (!formExtraText) {
            return _jsx(_Fragment, {}, void 0);
        }
        return (_jsx(FormExtraContainer, { children: _jsx(PrimaryText, __assign({ fontSize: 14, lineHeight: 20, color: color }, { children: formExtraText }), void 0) }, void 0));
    }, [
        vaultOption,
        asset,
        canCompleteWithdraw,
        migrateSourceVault,
        color,
        decimals,
        depositBalanceInAsset,
        hideCompleteWithdrawReminder,
        hideMigrationForm,
        lockedBalanceInAsset,
        vaultActionForm.actionType,
        vaultActionForm.receiveVault,
        vaultActionForm.vaultOption,
        vaultActionForm.withdrawOption,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(FormContainer, { children: content }, void 0), formExtra] }, void 0));
};
export default VaultV2ActionsForm;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=VaultV2ActionForm.js.map